import { serializeAppTheme, type HubTextSize } from './appTheme';
import { getKeycloakConfig } from './keycloakConfig';
import { keycloakHttpRequest } from './keycloakHttp';

type AccountAttributes = Record<string, string[]>;

type AccountRepresentation = {
	id?: string;
	username?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	emailVerified?: boolean;
	attributes?: AccountAttributes;
};

function accountUrl(): string {
	const { url, realm } = getKeycloakConfig();
	const base = url.replace(/\/$/, '');

	return `${base}/realms/${realm}/account`;
}

async function fetchAccount(accessToken: string): Promise<AccountRepresentation> {
	const response = await keycloakHttpRequest(accountUrl(), {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${accessToken}`,
			Accept: 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error(
			response.status === 401
				? 'Keycloak account GET failed: 401 (access token needs audience "account" — add audience-account mapper, then re-login)'
				: `Keycloak account GET failed: ${response.status}`,
		);
	}

	return (await response.json()) as AccountRepresentation;
}

/**
 * Merges `app_theme` into the current account representation and POSTs it back.
 * GET-then-POST avoids wiping firstName / lastName / other attributes.
 *
 * On native, requests use Capacitor HTTP so WebView CORS does not apply
 * (Account API CORS is stricter than custom SPIs like FCM/OTP).
 */
export async function updateAccountAppTheme(
	accessToken: string,
	fontScale: HubTextSize,
): Promise<void> {
	const account = await fetchAccount(accessToken);
	const attributes: AccountAttributes = { ...(account.attributes ?? {}) };
	attributes.app_theme = [serializeAppTheme(fontScale)];

	const body: AccountRepresentation = {
		...account,
		attributes,
	};

	const response = await keycloakHttpRequest(accountUrl(), {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${accessToken}`,
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(body),
	});

	if (!response.ok && response.status !== 204) {
		throw new Error(`Keycloak account POST failed: ${response.status}`);
	}
}
